import { useRef, useState, useEffect } from "react";
import { Button } from "@mui/material";
import { CloudUpload, Description, Delete } from "@mui/icons-material";

import { IBenefitsPolicy } from "../../Setup.types";
import { Title, Row } from "./BenefitsPolicy.styled";
import { UploadContainer, FilePreview, FileInfo, FileName, FileSize, ActionButton } from "./BenefitsPolicy.Custom.styled";
import api from "services/api";

interface IProps {
  benefitsPolicy: IBenefitsPolicy;
  handleChange: (payload: Partial<IBenefitsPolicy>) => void;
}

export function BenefitsPolicyCustom({ benefitsPolicy, handleChange }: IProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [existingDocument, setExistingDocument] = useState(benefitsPolicy.customDocument);

  // Atualizar documento existente quando benefitsPolicy mudar
  useEffect(() => {
    setExistingDocument(benefitsPolicy.customDocument);
  }, [benefitsPolicy.customDocument]);

  // Se há um arquivo local carregado (novo upload)
  const hasLocalFile = !!uploadedFile;

  // Se há um documento existente no backend (sem estar marcado para delete)
  const hasExistingDocument = !!existingDocument && !existingDocument.shouldDelete && !hasLocalFile;

  // Se há qualquer documento para exibir
  const hasDocument = hasLocalFile || hasExistingDocument;

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (file) {
      // Validar se é PDF
      if (file.type !== "application/pdf") {
        alert("Por favor, selecione apenas arquivos PDF");
        return;
      }

      // Validar tamanho (máximo 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert("O arquivo deve ter no máximo 10MB");
        return;
      }

      setUploadedFile(file);

      // Criar URL para preview
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);

      // Atualizar estado global com novo arquivo
      handleChange({
        customDocument: {
          file,
          fileName: file.name,
          fileSize: file.size,
          shouldDelete: false,
        }
      });
    }
  }

  function handleRemoveFile() {
    // Limpar arquivo local se existir
    if (hasLocalFile) {
      setUploadedFile(null);

      // Limpar URL do preview
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
        setFilePreviewUrl(null);
      }

      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }

    // Se havia documento existente (seja local ou no servidor), marca para deleção
    if (existingDocument?.documentId) {
      handleChange({
        customDocument: {
          ...existingDocument,
          shouldDelete: true
        }
      });
    } else {
      // Se não tinha documento no servidor, apenas remove do estado
      handleChange({ customDocument: undefined });
    }
  }

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  async function handleViewFile() {
    if (hasLocalFile && filePreviewUrl) {
      window.open(filePreviewUrl, "_blank");
      return;
    }

    if (!hasExistingDocument) return;

    if (existingDocument?.url) {
      window.open(existingDocument.url, "_blank");
      return;
    }

    if (existingDocument?.documentId) {
      // URL não está disponível — busca o arquivo via API autenticada
      // Abre a aba de forma síncrona primeiro para evitar bloqueio de popup
      const newTab = window.open("", "_blank");
      try {
        const response = await api.get(
          `/v2/workspaces/benefits-policy/document/${existingDocument.documentId}`,
          { responseType: "blob" }
        );
        const blobUrl = URL.createObjectURL(response.data);
        if (newTab) {
          newTab.location.href = blobUrl;
        }
      } catch (error) {
        console.error("[handleViewFile] Erro ao buscar documento:", error);
        if (newTab) newTab.close();
      }
    }
  }

  // Determinar informações do arquivo a exibir
  const displayFileName = hasLocalFile
    ? uploadedFile!.name
    : existingDocument?.fileName || "";

  const displayFileSize = hasLocalFile
    ? uploadedFile!.size
    : existingDocument?.fileSize || 0;

  function formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  return (
    <>
      <Title>Regra personalizada</Title>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {!hasDocument ? (
        <UploadContainer onClick={handleUploadClick}>
          <CloudUpload style={{ fontSize: 48, color: "#3024E1", marginBottom: 16 }} />
          <Title style={{ marginBottom: 8 }}>Clique para fazer upload do documento PDF</Title>
          <span style={{ fontSize: 14, color: "#666" }}>
            Tamanho máximo: 10MB
          </span>
        </UploadContainer>
      ) : (
        <FilePreview>
          <Description style={{ fontSize: 48, color: "#3024E1" }} />

          <FileInfo>
            <FileName>{displayFileName}</FileName>
            <FileSize>{formatFileSize(displayFileSize)}</FileSize>
            {hasExistingDocument && (
              <span style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                Documento salvo no servidor
              </span>
            )}
            {hasLocalFile && (
              <span style={{ fontSize: 12, color: "#3024E1", marginTop: 4 }}>
                Novo documento (será salvo ao clicar em Salvar)
              </span>
            )}
          </FileInfo>

          <Row style={{ gap: 12, marginLeft: "auto" }}>
            <ActionButton onClick={handleViewFile} variant="outlined">
              Visualizar
            </ActionButton>
            <ActionButton onClick={handleRemoveFile} variant="outlined" color="error">
              <Delete style={{ marginRight: 4 }} />
              Remover
            </ActionButton>
          </Row>
        </FilePreview>
      )}
    </>
  );
}
